import { useEffect, useState } from 'react';
import {
  Plus, Save, Power, EyeOff, Pencil, Trash2, Rows3,
  Home, Sun, Lightbulb, Image as ImageIcon,
} from 'lucide-react';
import supabase from '../lib/supabase';
import PhotoUploader from '../components/PhotoUploader';
import {
  PageHeader, Section, Badge, Button, EmptyState, Modal, Field, Input, Select, Checkbox, StatusLine,
} from '../components/ui';

type Court = {
  id: string;
  club_id: string;
  venue_id: string;
  name: string;
  surface: string;
  is_indoor: boolean;
  has_lights: boolean;
  photos: string[] | null;
  hourly_rate_offpeak: number;
  hourly_rate_peak: number;
  currency: string;
  slot_minutes: number;
  min_advance_hours: number;
  max_advance_days: number;
  is_active: boolean;
};

type Venue = { id: string; name: string; is_primary: boolean; is_active: boolean };

const SURFACES = ['hard', 'clay', 'grass', 'carpet', 'astro-turf'] as const;

export default function ClubCourts({ clubId }: { clubId: string }) {
  const [courts, setCourts] = useState<Court[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Court | 'new' | null>(null);

  const load = async () => {
    setLoading(true);
    const [courtsRes, venuesRes] = await Promise.all([
      supabase.from('courts').select('*').eq('club_id', clubId).order('name'),
      supabase.from('club_venues').select('id, name, is_primary, is_active').eq('club_id', clubId),
    ]);
    setCourts((courtsRes.data as Court[]) ?? []);
    setVenues((venuesRes.data as Venue[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [clubId]);

  const toggleActive = async (c: Court) => {
    await supabase.from('courts').update({ is_active: !c.is_active }).eq('id', c.id);
    load();
  };

  const del = async (c: Court) => {
    if (!confirm(`Hide court "${c.name}" from customers? Past bookings are preserved.`)) return;
    await supabase.from('courts').update({ is_active: false }).eq('id', c.id);
    load();
  };

  return (
    <div className="p-8 max-w-6xl">
      <PageHeader
        eyebrow="Club"
        title="Courts"
        subtitle="Add, edit, and price your courts. Customers only see active courts on your club page."
        actions={
          <Button icon={Plus} onClick={() => setEditing('new')}>Add court</Button>
        }
      />

      <Section className="overflow-hidden p-0">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading courts…</div>
        ) : courts.length === 0 ? (
          <EmptyState
            icon={Rows3}
            title="No courts yet"
            description="Add your first court so customers can book time at your club."
            action={<Button icon={Plus} onClick={() => setEditing('new')}>Add your first court</Button>}
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Court</th>
                <th className="text-left px-5 py-3 font-semibold">Venue</th>
                <th className="text-left px-5 py-3 font-semibold">Surface & features</th>
                <th className="text-left px-5 py-3 font-semibold">Rates (AED/hr)</th>
                <th className="text-left px-5 py-3 font-semibold">Slot</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {courts.map((c) => (
                <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50/60 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center text-gray-400 shrink-0">
                        {c.photos && c.photos[0]
                          ? <img src={c.photos[0]} className="w-full h-full object-cover" />
                          : <ImageIcon className="w-4 h-4" />}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">{c.name}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5 flex-wrap">
                          {c.is_indoor
                            ? <Badge tone="blue" icon={Home}>Indoor</Badge>
                            : <Badge tone="gray" icon={Sun}>Outdoor</Badge>}
                          {c.has_lights && <Badge tone="amber" icon={Lightbulb}>Lights</Badge>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-gray-700">
                    {venues.find((v) => v.id === c.venue_id)?.name ?? <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-gray-700 capitalize">{c.surface}</div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-gray-900">
                      <span className="text-xs text-gray-500 mr-1">Off-peak</span>
                      {Number(c.hourly_rate_offpeak).toFixed(0)}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Peak {Number(c.hourly_rate_peak).toFixed(0)}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-gray-700">{c.slot_minutes} min</td>
                  <td className="px-5 py-3">
                    <button onClick={() => toggleActive(c)} className="cursor-pointer">
                      {c.is_active
                        ? <Badge tone="green" icon={Power}>Active</Badge>
                        : <Badge tone="gray" icon={EyeOff}>Hidden</Badge>}
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="secondary" icon={Pencil} onClick={() => setEditing(c)}>Edit</Button>
                      <Button size="sm" variant="danger" icon={Trash2} onClick={() => del(c)}>Hide</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {editing && (
        <CourtForm
          clubId={clubId}
          court={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function CourtForm({
  clubId, court, onClose, onSaved,
}: {
  clubId: string;
  court: Court | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !court;
  const [name, setName] = useState(court?.name ?? '');
  const [surface, setSurface] = useState<string>(court?.surface ?? 'hard');
  const [isIndoor, setIsIndoor] = useState(court?.is_indoor ?? false);
  const [hasLights, setHasLights] = useState(court?.has_lights ?? true);
  const [offpeakRate, setOffpeakRate] = useState(String(court?.hourly_rate_offpeak ?? ''));
  const [peakRate, setPeakRate] = useState(String(court?.hourly_rate_peak ?? ''));
  const [slotMinutes, setSlotMinutes] = useState(String(court?.slot_minutes ?? 60));
  const [minAdvanceHours, setMinAdvanceHours] = useState(String(court?.min_advance_hours ?? 1));
  const [maxAdvanceDays, setMaxAdvanceDays] = useState(String(court?.max_advance_days ?? 30));
  const [photos, setPhotos] = useState<string[]>(court?.photos ?? []);
  const [isActive, setIsActive] = useState(court?.is_active ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [courtId, setCourtId] = useState<string | null>(court?.id ?? null);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [venueId, setVenueId] = useState<string>(court?.venue_id ?? '');

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('club_venues')
        .select('id, name, is_primary, is_active')
        .eq('club_id', clubId)
        .eq('is_active', true)
        .order('is_primary', { ascending: false })
        .order('name', { ascending: true });
      const list = (data ?? []) as Venue[];
      setVenues(list);
      if (!venueId && list.length > 0) {
        setVenueId(list.find((v) => v.is_primary)?.id ?? list[0].id);
      }
    })();
  }, [clubId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!venueId) { setErr('Pick a venue for this court.'); return; }
    setBusy(true);
    setErr(null);

    const payload = {
      club_id: clubId,
      venue_id: venueId,
      name,
      surface,
      is_indoor: isIndoor,
      has_lights: hasLights,
      hourly_rate_offpeak: Number(offpeakRate),
      hourly_rate_peak: Number(peakRate),
      slot_minutes: Number(slotMinutes),
      min_advance_hours: Number(minAdvanceHours),
      max_advance_days: Number(maxAdvanceDays),
      photos,
      is_active: isActive,
    };

    if (isNew) {
      const { data, error } = await supabase
        .from('courts')
        .insert(payload)
        .select('id')
        .single();
      setBusy(false);
      if (error) { setErr(error.message); return; }
      setCourtId(data.id);
      onSaved();
    } else {
      const { error } = await supabase
        .from('courts')
        .update(payload)
        .eq('id', court!.id);
      setBusy(false);
      if (error) { setErr(error.message); return; }
      onSaved();
    }
  };

  const photoPath = courtId ? `${clubId}/${courtId}` : null;

  return (
    <Modal
      title={isNew ? 'Add court' : `Edit ${court?.name}`}
      subtitle={isNew
        ? 'Create the court first, then come back to attach photos.'
        : 'Changes apply to all future bookings.'}
      onClose={onClose}
      wide
    >
      <form onSubmit={submit} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Venue"
            className="sm:col-span-2"
            hint={venues.length <= 1
              ? 'Courts belong to a venue. Add more venues to this club to move courts between them.'
              : 'Courts can be reassigned to a different venue at any time.'}>
            <Select value={venueId} onChange={(e) => setVenueId(e.target.value)} required>
              {venues.length === 0 && <option value="">No active venues — add one first</option>}
              {venues.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}{v.is_primary ? ' (primary)' : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Court name" className="sm:col-span-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Court 1" />
          </Field>

          <Field label="Surface">
            <Select value={surface} onChange={(e) => setSurface(e.target.value)} className="capitalize">
              {SURFACES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
            </Select>
          </Field>

          <Field label="Slot length (minutes)">
            <Select value={slotMinutes} onChange={(e) => setSlotMinutes(e.target.value)}>
              <option value="30">30</option>
              <option value="60">60</option>
              <option value="90">90</option>
              <option value="120">120</option>
            </Select>
          </Field>
        </div>

        <div className="flex flex-wrap gap-5 py-2 border-y border-gray-100">
          <Checkbox checked={isIndoor} onChange={setIsIndoor} label="Indoor" />
          <Checkbox checked={hasLights} onChange={setHasLights} label="Lights (evening play)" />
          <Checkbox checked={isActive} onChange={setIsActive} label="Visible to customers" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Off-peak rate (AED/hr)">
            <Input type="number" min="0" step="1" value={offpeakRate} onChange={(e) => setOffpeakRate(e.target.value)} required />
          </Field>
          <Field label="Peak rate (AED/hr)">
            <Input type="number" min="0" step="1" value={peakRate} onChange={(e) => setPeakRate(e.target.value)} required />
          </Field>
          <Field label="Min advance (hours)">
            <Input type="number" min="0" step="1" value={minAdvanceHours} onChange={(e) => setMinAdvanceHours(e.target.value)} />
          </Field>
          <Field label="Max advance (days)">
            <Input type="number" min="1" step="1" value={maxAdvanceDays} onChange={(e) => setMaxAdvanceDays(e.target.value)} />
          </Field>
        </div>

        <div>
          <div className="text-xs font-medium text-gray-700 mb-2">Court photos</div>
          {photoPath ? (
            <PhotoUploader
              bucket="court-photos"
              pathPrefix={photoPath}
              photos={photos}
              onChange={async (next) => {
                setPhotos(next);
                if (!isNew && court) {
                  await supabase.from('courts').update({ photos: next }).eq('id', court.id);
                }
              }}
              max={6}
            />
          ) : (
            <div className="text-xs text-gray-500 bg-gray-50 border border-dashed border-gray-300 rounded-lg p-4">
              Save the court first, then reopen this form to add photos.
            </div>
          )}
        </div>

        {err && <StatusLine kind="error">{err}</StatusLine>}

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
          <Button type="submit" loading={busy} icon={Save} className="flex-1">
            {isNew ? 'Create court' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
